import { useQuery } from "@apollo/client/react";
import {
  AddressFormatDocument,
  AreasDocument,
} from "@repo/graphql/documents/address/query.generated";
import { useMemo } from "react";

// The backend caps one request at this many areas; several large regions
// selected together can exceed 1000 address-level areas.
export const ADDRESS_LEVEL_AREAS_LIMIT = 5000;

/**
 * The address-level areas (communes, municipalities, districts...) inside the
 * given regions. Nothing is requested while no region is selected. Countries
 * whose address level is the top level have none to list, since their regions
 * already cover it.
 */
export default function useAddressLevelAreas(
  countryCode: string | null | undefined,
  regionIds: ReadonlyArray<string>,
) {
  const { data: formatData, loading: formatLoading } = useQuery(
    AddressFormatDocument,
    {
      variables: { countryCode: countryCode ?? "" },
      skip: !countryCode,
    },
  );
  const addressLevel = useMemo(
    () =>
      formatData?.address.addressFormat?.levels.find(
        (level) => level.isAddressLevel,
      ) ?? null,
    [formatData],
  );
  const depth =
    addressLevel && addressLevel.depth > 1 ? addressLevel.depth : null;
  const parentIds = useMemo(() => [...regionIds].sort(), [regionIds]);
  const noRegions = parentIds.length === 0;
  const {
    data,
    previousData,
    loading: areasLoading,
  } = useQuery(AreasDocument, {
    variables: {
      countryCode: countryCode ?? "",
      parentIds,
      depth,
      limit: ADDRESS_LEVEL_AREAS_LIMIT,
    },
    skip: !countryCode || depth === null || noRegions,
  });
  // Keep the previous selection's areas on screen while the next one loads.
  const areas = useMemo(
    () => (noRegions ? [] : ((data ?? previousData)?.address.areas ?? [])),
    [noRegions, data, previousData],
  );
  return {
    areas,
    levelName: addressLevel?.name ?? null,
    hasLevel: depth !== null,
    loading: formatLoading || areasLoading,
  };
}
